import { isAvailableNow } from "@/lib/is-available-now"
import { timeBoundHHMM } from "@/lib/schedule"
import { normalizeWeekHours } from "@/lib/scheduling/normalize-hours"
import type { Availability, Category, ItemOffer } from "@/types"

/**
 * Visibility options. With scheduling on, the channel serves a FUTURE window,
 * so "closed at this hour" stops being a reason to hide anything: the item
 * stays on the shelf carrying its window as a label, and the slot list (plus
 * the checkout) decides what the customer may actually order.
 */
export type VisibilityOptions = { enforceHours?: boolean; now?: Date; timeZone?: string | null }

/**
 * Visibility = "does this belong on the menu right now?".
 *
 * Only the scheduled-hours window decides it: a category or an offer whose
 * `availabilityId` window is closed for the current day+time is not rendered
 * at all — the customer never sees it.
 *
 * That is deliberately narrower than orderability (`isOfferAvailableNow`),
 * which ALSO rejects the manual out-of-stock flag: an `UNAVAILABLE` offer
 * stays on the shelf marked unavailable, because the merchant wants the
 * customer to know the item exists and is only momentarily out. A closed
 * window is the opposite — the item is simply not on the menu at this hour.
 *
 * Category `status` is the exception: an `UNAVAILABLE` category has always
 * been hidden whole (it was never rendered as a struck-through section), so
 * it counts as invisible here too.
 *
 * No window (`availabilityId` empty/undefined) => always visible, per
 * `isAvailableNow`.
 */
export function isCategoryVisibleNow(
  category: Pick<Category, "status" | "availabilityId">,
  availabilities: Availability[],
  options: VisibilityOptions = {},
): boolean {
  if (category.status === "UNAVAILABLE") return false
  if (options.enforceHours === false) return true
  return isAvailableNow(category.availabilityId, availabilities, options.now, options.timeZone)
}

/**
 * Same rule for an offer. Pass the owning `category` wherever the caller knows
 * it (flat search lists, deep links): a whole section can be time-gated, and
 * its offers must disappear with it instead of leaking out of the section that
 * hides them.
 */
export function isOfferVisibleNow(
  offer: Pick<ItemOffer, "availabilityId">,
  availabilities: Availability[],
  category?: Pick<Category, "status" | "availabilityId"> | null,
  options: VisibilityOptions = {},
): boolean {
  if (category && !isCategoryVisibleNow(category, availabilities, options)) return false
  if (options.enforceHours === false) return true
  return isAvailableNow(offer.availabilityId, availabilities, options.now, options.timeZone)
}

/**
 * Hours range label for an entity whose availability windows are narrower than
 * the day. Null when the entity has no window, so nothing is labelled "always".
 */
export function availabilityRangeLabel(
  availabilityIds: string[] | undefined,
  availabilities: Availability[],
  timeZone?: string | null,
): string | null {
  if (!availabilityIds?.length) return null

  let earliest: string | null = null
  let latest: string | null = null

  for (const id of availabilityIds) {
    const availability = availabilities.find((a) => a.id === id)
    if (!availability) continue
    for (const window of normalizeWeekHours(availability.hours, timeZone)) {
      const start = timeBoundHHMM(window.startTime)
      const end = timeBoundHHMM(window.endTime)
      if (!earliest || start < earliest) earliest = start
      if (!latest || end > latest) latest = end
    }
  }

  if (!earliest || !latest) return null
  if (earliest === "00:00" && latest >= "23:59") return null
  return `${earliest} às ${latest}`
}

/**
 * `offerId → owning category`, built in one pass. `findCategoryForOffer` scans
 * every category per offer, which is fine for a single lookup but quadratic
 * over a whole catalog — the search list filters hundreds of offers at a time.
 */
export function buildCategoryByOfferId(categories: Category[]): Map<string, Category> {
  const byOfferId = new Map<string, Category>()

  for (const category of categories) {
    for (const offerId of category.itemOfferId ?? []) {
      byOfferId.set(offerId, category)
    }
  }

  return byOfferId
}
